
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EditIcon, History } from 'lucide-react';
import { ReportData } from '@/types/report';
import { ReportHeader } from '@/components/reports/ReportHeader';
import { ClassInfoSection } from '@/components/reports/ClassInfoSection';
import { ReportContentSection } from '@/components/reports/ReportContentSection';
import { ReportDetailsTable } from '@/components/reports/ReportDetailsTable';
import { ReportChangeLogsDialog } from '@/components/reports/ReportChangeLogsDialog';
import { formatDate, formatTime, generateReportText } from '@/utils/reportFormatters';
import { useAuth } from '@/context/auth/AuthContext';
import { ReportUpdateLogs } from '@/components/admin/ReportUpdateLogs';
import { useQuery } from '@tanstack/react-query';

const ReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState<boolean>(false);
  const { toast } = useToast();
  const { user, checkAdminStatus } = useAuth();
  const { t } = useTranslation();

  const { 
    data: report, 
    isLoading, 
    refetch 
  } = useQuery({
    queryKey: ['report-detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No report ID provided');
      
      const { data, error } = await supabase
        .from('student_development_reports')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as ReportData;
    },
  });

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        const isUserAdmin = await checkAdminStatus();
        setIsAdmin(isUserAdmin);
      }
    };

    checkAdmin();
  }, [user, checkAdminStatus]);

  // ครู = เจ้าของ report → ก็แก้ได้ (จะเป็น PENDING request)
  const isOwner = useMemo(
    () => !!user?.email && !!report?.submitter_email && user.email === report.submitter_email,
    [user, report]
  );
  const canEdit = isAdmin || isOwner;

  const handleReportUpdated = () => {
    refetch();
    toast({
      title: t('reports.updatedTitle'),
      description: t('reports.updatedDesc'),
    });
  };

  const reportText = report ? generateReportText(report) : '';

  if (isLoading) {
    return (
      <div className="w-full px-4 py-8 flex justify-center">
        <div className="animate-pulse text-gray-500">{t('reports.loading')}</div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8">
      {report ? (
        <Card className="shadow-lg">
          <ReportHeader
            report={report}
            formatDate={formatDate}
            adminControls={canEdit ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:text-blue-600"
                  onClick={() => setLogsDialogOpen(true)}
                >
                  <History className="h-4 w-4 mr-1" />
                  ดูประวัติการแก้ไข
                </Button>
                <Button variant="ghost" size="sm" asChild className="hover:text-blue-600">
                  <Link to={`/reports/${report.id}/edit`}>
                    <EditIcon className="h-4 w-4 mr-1" />
                    {isAdmin ? 'แก้ไข' : 'ขอแก้ไข'}
                  </Link>
                </Button>
              </div>
            ) : undefined}
          />

          {canEdit && id && (
            <ReportChangeLogsDialog
              reportId={id}
              open={logsDialogOpen}
              onOpenChange={setLogsDialogOpen}
            />
          )}
          <CardContent className="p-6">
            <ClassInfoSection 
              report={report}
              formatDate={formatDate}
              formatTime={formatTime}
            />
            <ReportContentSection reportText={reportText} report={report} />
            <ReportDetailsTable report={report} />
            
            {isAdmin && id && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">{t('reports.editHistory')}</h3>
                <ReportUpdateLogs reportId={id} />
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-gray-500 py-8">{t('reports.notFound')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportDetail;
